import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Plane, Ship, Truck, ArrowRight, ArrowLeft, CheckCircle2, 
  Package, MapPin, User, Mail, Phone, Building, Search, Loader2,
  Box, Warehouse, HelpCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import SEO from '@/components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Location Search Component with Autocomplete
const LocationSearch = ({ value, onChange, placeholder, label, testId }) => {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      return;
    }

    const searchLocations = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/locations/search?q=${encodeURIComponent(query)}`);
        setResults(response.data.slice(0, 10));
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(searchLocations, 300);
    return () => clearTimeout(debounce);
  }, [query]);

  const handleSelect = (location) => {
    const displayValue = location.type === 'port' 
      ? `${location.city}, ${location.country} (${location.port_code})`
      : `${location.city}, ${location.country} (${location.airport_code})`;
    
    setQuery(displayValue);
    onChange({
      city: location.city,
      country: location.country,
      code: location.type === 'port' ? location.port_code : location.airport_code,
      type: location.type,
      name: location.type === 'port' ? location.port_name : location.airport_name
    });
    setShowDropdown(false);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <Label className="text-slate-700 font-medium">{label}</Label>
      <div className="relative mt-2">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          placeholder={placeholder}
          className="pl-10 h-12 border-slate-200 focus:border-electric focus:ring-electric/20"
          data-testid={testId}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-electric animate-spin" />
        )}
      </div>
      
      {showDropdown && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-60 overflow-y-auto">
          {results.map((location, index) => (
            <button
              key={`${location.type}-${location.type === 'port' ? location.port_code : location.airport_code}-${index}`}
              onClick={() => handleSelect(location)}
              className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-start gap-3 border-b border-slate-100 last:border-0 transition-colors"
            >
              {location.type === 'port' ? (
                <Ship className="w-4 h-4 text-cyan mt-1 flex-shrink-0" />
              ) : (
                <Plane className="w-4 h-4 text-electric mt-1 flex-shrink-0" />
              )}
              <div>
                <div className="font-medium text-slate-900">
                  {location.city}, {location.country}
                </div>
                <div className="text-xs text-slate-500">
                  {location.type === 'port' ? (
                    <span>{location.port_name} ({location.port_code})</span>
                  ) : (
                    <span>{location.airport_name} ({location.airport_code})</span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const QuotePage = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [quoteNumber, setQuoteNumber] = useState('');
  
  const [formData, setFormData] = useState({
    // Step 1: Mode
    transport_mode: '',
    // Step 2: Route
    origin_city: '',
    origin_country: '',
    origin_code: '',
    destination_city: '',
    destination_country: '',
    destination_code: '',
    // Step 3: Cargo
    cargo_type: 'general',
    cargo_description: '',
    weight_kg: '',
    volume_cbm: '',
    quantity: '',
    is_dangerous: false,
    // Step 4: Contact
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    company_name: ''
  });

  const transportModes = [
    { value: 'air', label: t('quotePage.modes.air'), icon: Plane, description: t('quotePage.modes.airDesc') },
    { value: 'sea', label: t('quotePage.modes.sea'), icon: Ship, description: t('quotePage.modes.seaDesc') },
    { value: 'land', label: t('quotePage.modes.land'), icon: Truck, description: t('quotePage.modes.landDesc') },
    { value: 'warehousing', label: t('quotePage.modes.warehousing'), icon: Warehouse, description: t('quotePage.modes.warehousingDesc') },
    { value: 'not_sure', label: t('quotePage.modes.notSure'), icon: HelpCircle, description: t('quotePage.modes.notSureDesc') }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleOriginChange = (location) => {
    setFormData(prev => ({
      ...prev,
      origin_city: location.city,
      origin_country: location.country,
      origin_code: location.code
    }));
  };

  const handleDestinationChange = (location) => {
    setFormData(prev => ({
      ...prev,
      destination_city: location.city,
      destination_country: location.country,
      destination_code: location.code
    }));
  };

  const validateStep = () => {
    if (step === 1) {
      return !!formData.transport_mode;
    } else if (step === 2) {
      return formData.origin_city && formData.origin_country && 
             formData.destination_city && formData.destination_country;
    } else if (step === 3) {
      return formData.cargo_description || formData.weight_kg || formData.quantity;
    } else if (step === 4) {
      return formData.contact_name && formData.contact_email && formData.contact_phone;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep()) {
      toast.error(t('quotePage.toastRequired'));
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        weight_kg: parseFloat(formData.weight_kg) || 0,
        volume_cbm: formData.volume_cbm ? parseFloat(formData.volume_cbm) : null,
        quantity: parseInt(formData.quantity) || 1
      };
      
      const response = await axios.post(`${API}/quotes`, payload);
      setQuoteNumber(response.data.quote_number);
      setSubmitted(true);
      toast.success(t('quotePage.toastSuccess'));
    } catch (error) {
      console.error('Failed to submit quote:', error);
      toast.error(t('quotePage.toastError'));
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (validateStep()) {
      setStep(prev => Math.min(prev + 1, 4));
    } else {
      toast.error(t('quotePage.toastIncomplete'));
    }
  };

  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen pt-32 pb-20 bg-slate-50" data-testid="quote-success">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-lg mx-auto text-center"
          >
            <div className="w-20 h-20 bg-cyan/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-cyan" />
            </div>
            <h1 className="font-space text-3xl font-bold text-slate-900 mb-4">
              {t('quotePage.successTitle')}
            </h1>
            <p className="text-slate-600 mb-6">
              {t('quotePage.successMessage')}
            </p>
            <div className="bg-white p-6 rounded-xl shadow-md mb-8 border border-slate-100">
              <p className="text-sm text-slate-500 mb-2">{t('quotePage.refNumber')}</p>
              <p className="text-2xl font-space font-bold text-slate-900" data-testid="quote-number">{quoteNumber}</p>
            </div>
            <div className="space-y-3">
              <p className="text-sm text-slate-500">
                {t('quotePage.questions')}{' '}
                <a href="mailto:info@elementarycargo.com" className="text-electric hover:underline">
                  info@elementarycargo.com
                </a>
              </p>
              <Button 
                onClick={() => window.location.href = '/'} 
                className="bg-electric hover:bg-electric/90 text-white px-8 py-3 rounded-lg"
                data-testid="back-home-btn"
              >
                {t('quotePage.backHome')}
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="quote-page">
      <SEO 
        titleKey="seo.quote.title"
        descriptionKey="seo.quote.description"
        path="/quote"
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/assets/hero-logistics.jpg"
            alt=""
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/85 via-slate-900/90 to-slate-900" />
        </div>
        <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-30" />
        {/* Cyan route line accent */}
        <div className="absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan/30 to-transparent" />
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-2xl mx-auto"
          >
            <span className="text-cyan text-sm font-semibold tracking-wider uppercase mb-4 block">
              {t('quotePage.badge')}
            </span>
            <h1 className="font-space text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {t('quotePage.heroTitle')}
            </h1>
            <p className="text-slate-400 text-lg">
              {t('quotePage.heroSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12">
        <div className="container-custom">
          {/* Progress Steps */}
          <div className="max-w-3xl mx-auto mb-12">
            <div className="flex items-center justify-between">
              {[t('quotePage.steps.mode'), t('quotePage.steps.route'), t('quotePage.steps.cargo'), t('quotePage.steps.contact')].map((label, index) => (
                <div key={label} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                      step > index + 1 
                        ? 'bg-cyan text-slate-900' 
                        : step === index + 1 
                          ? 'bg-electric text-white shadow-lg shadow-electric/30' 
                          : 'bg-slate-200 text-slate-500'
                    }`}>
                      {step > index + 1 ? <CheckCircle2 className="w-5 h-5" /> : index + 1}
                    </div>
                    <span className={`hidden sm:block mt-2 text-xs font-medium ${
                      step >= index + 1 ? 'text-slate-900' : 'text-slate-400'
                    }`}>
                      {label}
                    </span>
                  </div>
                  {index < 3 && (
                    <div className={`w-16 sm:w-24 h-1 mx-2 sm:mx-4 rounded-full ${
                      step > index + 1 ? 'bg-cyan' : 'bg-slate-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Form Card */}
          <Card className="max-w-3xl mx-auto shadow-xl border-0 rounded-2xl overflow-hidden" data-testid="quote-form-card">
            <CardContent className="p-8 md:p-10">
              <AnimatePresence mode="wait">
                {/* Step 1: Mode Selection */}
                {step === 1 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    data-testid="step-1"
                  >
                    <h2 className="font-space text-2xl font-bold text-slate-900 mb-2">
                      {t('quotePage.step1Title')}
                    </h2>
                    <p className="text-slate-500 mb-8">
                      {t('quotePage.step1Subtitle')}
                    </p>
                    
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {transportModes.map((mode) => (
                        <button
                          key={mode.value}
                          onClick={() => handleChange('transport_mode', mode.value)}
                          className={`p-6 rounded-xl border-2 transition-all text-left ${
                            formData.transport_mode === mode.value
                              ? 'border-electric bg-electric/5 shadow-lg shadow-electric/10'
                              : 'border-slate-200 hover:border-electric/50 hover:shadow-md'
                          }`}
                          data-testid={`mode-${mode.value}`}
                        >
                          <mode.icon className={`w-8 h-8 mb-3 ${
                            formData.transport_mode === mode.value ? 'text-electric' : 'text-slate-400'
                          }`} />
                          <h3 className={`font-semibold mb-1 ${
                            formData.transport_mode === mode.value ? 'text-electric' : 'text-slate-900'
                          }`}>
                            {mode.label}
                          </h3>
                          <p className="text-sm text-slate-500">{mode.description}</p>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Step 2: Origin & Destination */}
                {step === 2 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    data-testid="step-2"
                  >
                    <h2 className="font-space text-2xl font-bold text-slate-900 mb-2">
                      {t('quotePage.step2Title')}
                    </h2>
                    <p className="text-slate-500 mb-8">
                      {t('quotePage.step2Subtitle')}
                    </p>
                    
                    <div className="space-y-8">
                      <div>
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-8 h-8 rounded-full bg-cyan/20 flex items-center justify-center">
                            <MapPin className="w-4 h-4 text-cyan" />
                          </div>
                          <span className="font-semibold text-slate-900">{t('quotePage.origin')}</span>
                        </div>
                        <LocationSearch
                          value={formData.origin_city ? `${formData.origin_city}, ${formData.origin_country}` : ''}
                          onChange={handleOriginChange}
                          placeholder={t('quotePage.locationPlaceholder')}
                          label={t('quotePage.locationLabel')}
                          testId="origin-search"
                        />
                        {formData.origin_code && (
                          <div className="mt-2 text-sm text-cyan flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4" />
                            {t('quotePage.codeLabel')} {formData.origin_code}
                          </div>
                        )}
                      </div>

                      <div className="relative">
                        <div className="absolute left-4 -top-4 -bottom-4 w-px bg-gradient-to-b from-cyan via-electric to-cyan" />
                      </div>

                      <div>
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-8 h-8 rounded-full bg-electric/20 flex items-center justify-center">
                            <MapPin className="w-4 h-4 text-electric" />
                          </div>
                          <span className="font-semibold text-slate-900">{t('quotePage.destination')}</span>
                        </div>
                        <LocationSearch
                          value={formData.destination_city ? `${formData.destination_city}, ${formData.destination_country}` : ''}
                          onChange={handleDestinationChange}
                          placeholder={t('quotePage.locationPlaceholder')}
                          label={t('quotePage.locationLabel')}
                          testId="destination-search"
                        />
                        {formData.destination_code && (
                          <div className="mt-2 text-sm text-electric flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4" />
                            {t('quotePage.codeLabel')} {formData.destination_code}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Cargo Details */}
                {step === 3 && (
                  <motion.div
                    key="step3"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    data-testid="step-3"
                  >
                    <h2 className="font-space text-2xl font-bold text-slate-900 mb-2">
                      {t('quotePage.step3Title')}
                    </h2>
                    <p className="text-slate-500 mb-8">
                      {t('quotePage.step3Subtitle')}
                    </p>
                    
                    <div className="space-y-6">
                      <div>
                        <Label className="text-slate-700 font-medium">{t('quotePage.whatShipping')}</Label>
                        <Textarea
                          value={formData.cargo_description}
                          onChange={(e) => handleChange('cargo_description', e.target.value)}
                          placeholder={t('quotePage.cargoPlaceholder')}
                          rows={3}
                          className="mt-2 border-slate-200 focus:border-electric focus:ring-electric/20"
                          data-testid="cargo-description"
                        />
                      </div>

                      <div className="grid sm:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-slate-700 font-medium">{t('quotePage.weight')}</Label>
                          <div className="relative mt-2">
                            <Input
                              type="number"
                              value={formData.weight_kg}
                              onChange={(e) => handleChange('weight_kg', e.target.value)}
                              placeholder="0"
                              className="pr-12 h-12 border-slate-200 focus:border-electric focus:ring-electric/20"
                              data-testid="weight-kg"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">kg</span>
                          </div>
                        </div>
                        <div>
                          <Label className="text-slate-700 font-medium">{t('quotePage.volume')}</Label>
                          <div className="relative mt-2">
                            <Input
                              type="number"
                              value={formData.volume_cbm}
                              onChange={(e) => handleChange('volume_cbm', e.target.value)}
                              placeholder="0"
                              className="pr-14 h-12 border-slate-200 focus:border-electric focus:ring-electric/20"
                              data-testid="volume-cbm"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">CBM</span>
                          </div>
                        </div>
                        <div>
                          <Label className="text-slate-700 font-medium">Quantity</Label>
                          <div className="relative mt-2">
                            <Input
                              type="number"
                              value={formData.quantity}
                              onChange={(e) => handleChange('quantity', e.target.value)}
                              placeholder={t('quotePage.quantityPlaceholder')}
                              className="h-12 border-slate-200 focus:border-electric focus:ring-electric/20"
                              data-testid="quantity"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Dangerous goods */}
                      <div className="bg-amber/10 border border-amber/30 p-4 rounded-xl">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            id="is_dangerous"
                            checked={formData.is_dangerous}
                            onCheckedChange={(checked) => handleChange('is_dangerous', checked)}
                            data-testid="dangerous-goods-checkbox"
                          />
                          <div className="grid gap-1.5 leading-none">
                            <label htmlFor="is_dangerous" className="text-sm font-medium text-slate-900 cursor-pointer">
                              {t('quotePage.dangerousGoods')}
                            </label>
                            <p className="text-xs text-slate-500">
                              {t('quotePage.dangerousGoodsDesc')}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 4: Contact Info */}
                {step === 4 && (
                  <motion.div
                    key="step4"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    data-testid="step-4"
                  >
                    <h2 className="font-space text-2xl font-bold text-slate-900 mb-2">
                      {t('quotePage.step4Title')}
                    </h2>
                    <p className="text-slate-500 mb-8">
                      {t('quotePage.responseNote')}
                    </p>
                    
                    <div className="space-y-5">
                      <div>
                        <Label className="flex items-center gap-2 text-slate-700 font-medium">
                          <User className="w-4 h-4 text-electric" /> {t('quotePage.fullName')}
                        </Label>
                        <Input
                          value={formData.contact_name}
                          onChange={(e) => handleChange('contact_name', e.target.value)}
                          placeholder={t('quotePage.fullNamePlaceholder')}
                          className="mt-2 h-12 border-slate-200 focus:border-electric focus:ring-electric/20"
                          data-testid="contact-name"
                        />
                      </div>
                      <div>
                        <Label className="flex items-center gap-2 text-slate-700 font-medium">
                          <Mail className="w-4 h-4 text-electric" /> {t('quotePage.email')}
                        </Label>
                        <Input
                          type="email"
                          value={formData.contact_email}
                          onChange={(e) => handleChange('contact_email', e.target.value)}
                          placeholder="your@email.com"
                          className="mt-2 h-12 border-slate-200 focus:border-electric focus:ring-electric/20"
                          data-testid="contact-email"
                        />
                      </div>
                      <div>
                        <Label className="flex items-center gap-2 text-slate-700 font-medium">
                          <Phone className="w-4 h-4 text-electric" /> {t('quotePage.phone')}
                        </Label>
                        <Input
                          value={formData.contact_phone}
                          onChange={(e) => handleChange('contact_phone', e.target.value)}
                          placeholder="+31 62 538 1824"
                          className="mt-2 h-12 border-slate-200 focus:border-electric focus:ring-electric/20"
                          data-testid="contact-phone"
                        />
                      </div>
                      <div>
                        <Label className="flex items-center gap-2 text-slate-700 font-medium">
                          <Building className="w-4 h-4 text-electric" /> {t('quotePage.company')}
                        </Label>
                        <Input
                          value={formData.company_name}
                          onChange={(e) => handleChange('company_name', e.target.value)}
                          placeholder={t('quotePage.companyPlaceholder')}
                          className="mt-2 h-12 border-slate-200 focus:border-electric focus:ring-electric/20"
                          data-testid="company-name"
                        />
                      </div>
                    </div>

                    {/* Trust message */}
                    <div className="mt-8 p-4 bg-slate-50 rounded-xl border border-slate-100">
                      <p className="text-sm text-slate-600 text-center">
                        <CheckCircle2 className="w-4 h-4 text-cyan inline mr-2" />
                        {t('quotePage.responseNote')}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Navigation */}
              <div className="flex justify-between mt-10 pt-6 border-t border-slate-100">
                {step > 1 ? (
                  <Button 
                    variant="outline" 
                    onClick={prevStep} 
                    className="flex items-center gap-2 border-slate-300 text-slate-700 hover:bg-slate-50 px-6 py-3 rounded-lg"
                    data-testid="prev-btn"
                  >
                    <ArrowLeft className="w-4 h-4" /> {t('quotePage.back')}
                  </Button>
                ) : (
                  <div />
                )}
                
                {step < 4 ? (
                  <Button 
                    onClick={nextStep} 
                    className="bg-electric hover:bg-electric/90 text-white flex items-center gap-2 px-8 py-3 rounded-lg shadow-lg shadow-electric/20"
                    data-testid="next-btn"
                  >
                    {t('quotePage.continue')} <ArrowRight className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button 
                    onClick={handleSubmit} 
                    className="bg-amber hover:bg-amber/90 text-slate-900 font-semibold flex items-center gap-2 px-8 py-3 rounded-lg shadow-lg shadow-amber/20"
                    disabled={loading}
                    data-testid="submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> {t('quotePage.submitting')}
                      </>
                    ) : (
                      <>
                        {t('quotePage.submit')} <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Trust indicators below form */}
          <div className="max-w-3xl mx-auto mt-8 text-center">
            <p className="text-sm text-slate-500">
              {t('quotePage.complianceLine')}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QuotePage;
